// src/components/admin/nornsweave/modals/AddAideModal.js
import React from 'react';
import { formatTime12 } from '../../../TimePicker';

function AddAideModal({
  aide,
  setAide,
  aideScheduleBlocks,
  onClose,
  onAdd
}) {
  const regularBlocks = aideScheduleBlocks.filter(b => b.scheduleType !== 'mass_day');
  const massDayBlocks = aideScheduleBlocks.filter(b => b.scheduleType === 'mass_day');

  const toggleBlock = (blockId) => {
    const currentBlocks = aide.assignedBlocks || [];
    if (currentBlocks.includes(blockId)) {
      setAide({ ...aide, assignedBlocks: currentBlocks.filter(id => id !== blockId) });
    } else {
      setAide({ ...aide, assignedBlocks: [...currentBlocks, blockId] });
    }
  };

  const isBlockSelected = (blockId) => {
    return (aide.assignedBlocks || []).includes(blockId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-slate-800 mb-4">Add Library Aide</h3>
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Aide Name *</label>
            <input
              type="text"
              required
              placeholder="Enter aide name"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={aide.name}
              onChange={(e) => setAide({...aide, name: e.target.value})}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-1">Email (optional)</label>
            <input
              type="email"
              placeholder="aide@school.edu"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              value={aide.email || ''}
              onChange={(e) => setAide({...aide, email: e.target.value})}
            />
          </div>

          {/* A/B Day Selection */}
          <div>
            <label className="block text-slate-700 text-sm font-medium mb-2">A/B Day Assignment *</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAide({...aide, dayType: 'A'})}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                  aide.dayType === 'A'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                A Day Only
              </button>
              <button
                type="button"
                onClick={() => setAide({...aide, dayType: 'B'})}
                className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                  aide.dayType === 'B'
                    ? 'bg-purple-600 text-white'
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                B Day Only
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {aide.dayType === 'A'
                ? 'This aide works only on A Days'
                : 'This aide works only on B Days'}
            </p>
          </div>

          {/* Block Selection */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Assign to Schedule Blocks *</h4>

            {/* Regular Blocks */}
            {regularBlocks.length > 0 && (
              <div className="mb-4">
                <h5 className="text-sm font-medium text-teal-700 mb-2">Regular Day Blocks</h5>
                <div className="space-y-2">
                  {regularBlocks.map(block => (
                    <label
                      key={block.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        isBlockSelected(block.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isBlockSelected(block.id)}
                        onChange={() => toggleBlock(block.id)}
                        className="w-4 h-4 text-teal-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-slate-800">{block.name}</span>
                        <span className="text-sm text-slate-600 ml-2">
                          {formatTime12(block.startTime)} - {formatTime12(block.endTime)}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({block.library === 'elementary' ? 'Elem' : block.library === 'middle' ? 'MS' : 'Both'})
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Mass Day Blocks */}
            {massDayBlocks.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-violet-700 mb-2">Mass Day Blocks</h5>
                <div className="space-y-2">
                  {massDayBlocks.map(block => (
                    <label
                      key={block.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        isBlockSelected(block.id)
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isBlockSelected(block.id)}
                        onChange={() => toggleBlock(block.id)}
                        className="w-4 h-4 text-violet-600"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-slate-800">{block.name}</span>
                        <span className="text-sm text-slate-600 ml-2">
                          {formatTime12(block.startTime)} - {formatTime12(block.endTime)}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({block.library === 'elementary' ? 'Elem' : block.library === 'middle' ? 'MS' : 'Both'})
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(aide.assignedBlocks || []).length === 0 && (
              <p className="text-sm text-amber-600 mt-2">Select at least one block</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 text-slate-700 py-2 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAdd}
              disabled={!aide.name || (aide.assignedBlocks || []).length === 0}
              className="flex-1 bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Add Aide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddAideModal;
